import { FEATURE_FLAG_PFS } from '@config/feature-flag';
import type {
  FulfillmentRestriction,
  ShippingRestrictions,
} from '@adapters/bigcommerce/types/product';

/**
 * A restriction that can't be read blocks both methods: shipping a pickup-only
 * item by mistake is worse than losing the sale.
 */
const unreadableRestriction = (value: unknown): FulfillmentRestriction => {
  console.error('Invalid fulfillment_restriction metafield', value);
  return {
    fulfillment_restriction_code: '',
    ship_to_address: false,
    pickup_location_ids: [],
  };
};

/**
 * Validates a raw `fulfillment_restriction` metafield value (already-parsed
 * object or JSON string). Null means "unrestricted, behave as before": no
 * metafield, or FEATURE_FLAG_PFS is off. Everything else fails closed: a value
 * that isn't a JSON object can't ship or be picked up anywhere, and a missing
 * `ship_to_address` means it can't ship.
 */
export const parseFulfillmentRestriction = (
  value: unknown
): FulfillmentRestriction | null => {
  if (!FEATURE_FLAG_PFS || value == null) return null;

  let parsed = value;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return unreadableRestriction(value);
    }
    // A stored JSON null reads the same as no metafield on every path.
    if (parsed === null) return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return unreadableRestriction(value);
  }

  const {
    fulfillment_restriction_code: code,
    ship_to_address: shipToAddress,
    pickup_location_ids: pickupLocationIds,
  } = parsed as Record<string, unknown>;

  return {
    fulfillment_restriction_code: code == null ? '' : String(code),
    ship_to_address: shipToAddress === true,
    pickup_location_ids: Array.isArray(pickupLocationIds)
      ? pickupLocationIds.map(Number).filter(Number.isFinite)
      : [],
  };
};

/**
 * Whether the item can go to a shipping address. The restriction can only take
 * shipping away: product-level rules still apply on top (an R5 item that ships
 * from the WDC still can't ship if the product says `shipping_allowed: false`).
 * Unrestricted items keep today's behaviour.
 */
export const canShipToAddress = (
  restriction: FulfillmentRestriction | null | undefined,
  shippingRestrictions?: Pick<ShippingRestrictions, 'shipping_allowed'> | null
): boolean => {
  if (!restriction) return true;
  return (
    restriction.ship_to_address &&
    shippingRestrictions?.shipping_allowed !== false
  );
};

/**
 * Whether the item can be picked up at the store, by MEC store number. This
 * overrides product-level `pickup_allowed`. The list only holds stores with
 * stock, but quantity is still checked against store inventory elsewhere.
 * Unrestricted items keep today's behaviour.
 */
export const canPickupAtStore = (
  restriction: FulfillmentRestriction | null | undefined,
  mecStoreId: string | number | null | undefined
): boolean => {
  if (!restriction) return true;
  if (mecStoreId == null || mecStoreId === '') return false;
  return restriction.pickup_location_ids.some(
    (id) => String(id) === String(mecStoreId)
  );
};

/** Whether a restricted item can be fulfilled online at all, by either method. */
export const canFulfillRestrictedItem = (
  restriction: FulfillmentRestriction,
  shippingRestrictions?: Pick<ShippingRestrictions, 'shipping_allowed'> | null
): boolean =>
  canShipToAddress(restriction, shippingRestrictions) ||
  restriction.pickup_location_ids.length > 0;
